/**
 * Visite de l'application (SAAS-1145) : les sections principales, présentées
 * depuis n'importe quel écran du shell.
 *
 * @remarks
 * Le rail (`Sidebar`) et le `Header` sont rendus sur tous les contextes
 * desktop et tablette : aucune préparation de mise en page n'est nécessaire.
 *
 * Ancres :
 *  - liens du rail → `[data-tour="nav-<id>"]` (posé une fois dans `Sidebar`,
 *    `id` = `NavigationLink.id` du manifest de la feature) ;
 *  - header → `[data-tour="header-*"]` (`CommandBar`, toggles de panneaux,
 *    déclencheur du menu compte).
 *
 * Étapes `optional` : Skill-up est derrière un feature flag ; les toggles de
 * panneaux dépendent du contexte (pas de panneau, ou toggle droit déplacé dans
 * le titre de la page sur les listes de tests).
 */

import { TOUR_IDS, type GuidedTourStep, type TourDefinition } from './tour-types';

const APP_OVERVIEW_STEPS: readonly GuidedTourStep[] = [
  {
    selector: '[data-tour="nav-tech-tests"]',
    title: 'Test technique',
    description:
      'Le catalogue des évaluations et tes tests en cours. C’est ici que tu démarres une épreuve.',
  },
  {
    selector: '[data-tour="nav-candidate-results"]',
    title: 'Résultats',
    description:
      'Tes rapports d’évaluation : la note, les points forts et les axes de progression, à relire à tout moment.',
  },
  {
    selector: '[data-tour="nav-cv"]',
    title: 'CV',
    description:
      'Ton CV d’adéquation et ta grille de compétences, construits à partir de tes évaluations et partageables avec un recruteur.',
  },
  {
    selector: '[data-tour="nav-skill-up"]',
    title: 'Skill-up',
    description:
      'Des sessions d’apprentissage guidées pour progresser sur une compétence, à ton rythme.',
    optional: true,
  },
  {
    selector: '[data-tour="nav-purchases"]',
    title: 'Boutique',
    description: 'Achète des évaluations ou des packs, et retrouve l’historique de tes achats.',
  },
  {
    selector: '[data-tour="nav-settings"]',
    title: 'Paramètres',
    description: 'Apparence, raccourcis clavier, préférences : personnalise ton espace de travail.',
  },
  {
    selector: '[data-tour="header-command-bar"]',
    title: 'Barre de commande',
    description:
      'Cherche une page ou lance une action au clavier, depuis n’importe où. Raccourci : Ctrl (ou ⌘) + K.',
  },
  {
    selector: '[data-tour="header-left-panel"]',
    title: 'Panneau gauche',
    description:
      'Ouvre ou replie les outils de la page : liste des tests, documents, navigation. Chaque page a les siens.',
    optional: true,
  },
  {
    selector: '[data-tour="header-right-panel"]',
    title: 'Panneau droit',
    description:
      'Le panneau de détails et d’aide de la page : guide, synthèse, conseils. Replie-le quand tu veux plus d’espace.',
    optional: true,
  },
  {
    selector: '[data-tour="header-user-menu"]',
    title: 'Mon compte',
    description:
      'Ton profil, l’aide et le contact de l’équipe, les mentions légales et la déconnexion. Tu peux relancer cette visite depuis ce menu.',
  },
];

export const APP_OVERVIEW_TOUR: TourDefinition = {
  id: TOUR_IDS.APP_OVERVIEW,
  steps: APP_OVERVIEW_STEPS,
};
